import type { WorkflowState } from '@/types/workflow';
import {
  COLLAPSED_CARD_WIDTH,
  COLLAPSED_CARD_HEIGHT_MIN as COLLAPSED_CARD_HEIGHT,
} from '@/components/editor/CollapsedGroupCard';

const DEFAULT_BLOCK_WIDTH = 200;
const DEFAULT_BLOCK_HEIGHT = 80;

/**
 * Compute the bounding box of all visible elements (blocks + collapsed groups).
 * Returns null if there are no elements.
 */
function computeBoundingBox(blocks: WorkflowState['blocks'], groups: WorkflowState['groups']) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  let hasAny = false;

  const halfW = DEFAULT_BLOCK_WIDTH / 2;
  const halfH = DEFAULT_BLOCK_HEIGHT / 2;

  for (const b of blocks) {
    hasAny = true;
    minX = Math.min(minX, b.x - halfW);
    minY = Math.min(minY, b.y - halfH);
    maxX = Math.max(maxX, b.x + halfW);
    maxY = Math.max(maxY, b.y + halfH);
  }

  const halfCW = COLLAPSED_CARD_WIDTH / 2;
  const halfCH = COLLAPSED_CARD_HEIGHT / 2;

  for (const g of groups) {
    if (g.collapsed && g.collapsedX != null && g.collapsedY != null) {
      hasAny = true;
      minX = Math.min(minX, g.collapsedX - halfCW);
      minY = Math.min(minY, g.collapsedY - halfCH);
      maxX = Math.max(maxX, g.collapsedX + halfCW);
      maxY = Math.max(maxY, g.collapsedY + halfCH);
    }
  }

  if (!hasAny) return null;

  return { minX, minY, maxX, maxY };
}

/**
 * Center a workflow so that the midpoint of all blocks is at (0, 0).
 * Shifts blocks, connector waypoints, and collapsed group positions by the computed delta.
 * Returns a new WorkflowState (does not mutate input).
 */
export function centerWorkflow(state: WorkflowState): WorkflowState {
  const bbox = computeBoundingBox(state.blocks, state.groups);
  if (!bbox) return state;

  const centerX = (bbox.minX + bbox.maxX) / 2;
  const centerY = (bbox.minY + bbox.maxY) / 2;

  // Already centered (within 1px tolerance)
  if (Math.abs(centerX) < 1 && Math.abs(centerY) < 1) return state;

  const dx = -centerX;
  const dy = -centerY;

  return {
    ...state,
    coordinateOrigin: 'center',
    blocks: state.blocks.map(b => ({
      ...b,
      x: b.x + dx,
      y: b.y + dy,
    })),
    connectors: state.connectors.map(c => ({
      ...c,
      waypoints: c.waypoints?.map(w => ({ x: w.x + dx, y: w.y + dy })),
    })),
    groups: state.groups.map(g => ({
      ...g,
      collapsedX: g.collapsedX != null ? g.collapsedX + dx : undefined,
      collapsedY: g.collapsedY != null ? g.collapsedY + dy : undefined,
    })),
  };
}

/**
 * Compute bounding-box edges for a single group based on its member blocks.
 * Returns { boundsLeft, boundsTop, boundsRight, boundsBottom } or undefined if no blocks found.
 */
export function computeGroupBounds(
  groupBlockIds: string[],
  allBlocks: WorkflowState['blocks'],
): { boundsLeft: number; boundsTop: number; boundsRight: number; boundsBottom: number } | undefined {
  const memberBlocks = allBlocks.filter(b => groupBlockIds.includes(b.id));
  if (memberBlocks.length === 0) return undefined;

  const halfW = DEFAULT_BLOCK_WIDTH / 2;
  const halfH = DEFAULT_BLOCK_HEIGHT / 2;

  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const b of memberBlocks) {
    minX = Math.min(minX, b.x - halfW);
    minY = Math.min(minY, b.y - halfH);
    maxX = Math.max(maxX, b.x + halfW);
    maxY = Math.max(maxY, b.y + halfH);
  }

  return { boundsLeft: minX, boundsTop: minY, boundsRight: maxX, boundsBottom: maxY };
}
